import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../contexts/AuthContext";
import { useRole } from "../../../contexts/RoleContext";
import EtiquetteCoach from "../../../components/feature/EtiquetteCoach";

const EDGE_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/gemini-npc`;

interface CollabMessage {
  id: string;
  workspace_id: string;
  sender_user_id: string;
  sender_role: string;
  receiver_role: string;
  task_id: string;
  task_title: string;
  subject: string;
  body: string;
  is_read: boolean;
  created_at: string;
}

const ROLE_LABEL: Record<string, string> = {
  AR: "A&R 담당",
  Finance: "재무 담당",
  Marketing: "마케팅 담당",
  Legal: "법무 담당",
  CEO: "대표이사",
  PD: "공연기획 PD",
  Ticketing: "티켓영업 MD",
  Projects: "사업팀장",
  Fundraising: "후원개발팀",
  Finance_npo: "회계담당",
  Evaluation: "사업평가위원",
  Editor: "에디터",
  Contract: "계약 MD",
  Creative: "크리에이티브 디렉터",
  AE: "영업 AE",
  Production: "영상 PD",
  Data: "데이터 분석가",
};

const ROLE_COLOR: Record<string, string> = {
  AR: "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/20",
  Marketing: "text-pink-400/80 bg-pink-400/10 border-pink-400/20",
  Legal: "text-amber-400/80 bg-amber-400/10 border-amber-400/20",
  Finance: "text-emerald-400/80 bg-emerald-400/10 border-emerald-400/20",
  PD: "text-violet-400/80 bg-violet-400/10 border-violet-400/20",
};
function getRoleColor(role: string) {
  return ROLE_COLOR[role] ?? "text-[#888888] bg-white/[0.05] border-white/[0.08]";
}

// AI 답장 초안 생성
async function generateAIReply(msg: CollabMessage, myRole: string): Promise<string> {
  const context = `당신은 ${ROLE_LABEL[myRole] ?? myRole} 직책을 맡고 있습니다. 아래 협업 요청 메일에 대한 답장 초안을 작성해주세요. 반드시 한국어로, 비즈니스 이메일 형식으로, 2-3단락으로 작성하세요.`;
  const userMessage = `[원본 메일]\n제목: ${msg.subject}\n발신: ${ROLE_LABEL[msg.sender_role] ?? msg.sender_role}\n수신: ${ROLE_LABEL[msg.receiver_role] ?? msg.receiver_role}\n관련 태스크: ${msg.task_title}\n\n내용:\n${msg.body}\n\n이 메일에 대해 협조적이고 구체적인 답장 초안을 작성해주세요.`;

  const res = await fetch(EDGE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ npcType: "mail_responder", userMessage, history: [], context }),
  });
  const data = await res.json();
  return data.reply ?? "답장 초안을 생성할 수 없습니다.";
}

interface MailDetailProps {
  selected: CollabMessage;
  jobRole: string;
  workspaceId: string | null;
  userId: string | undefined;
  formatTime: (iso: string) => string;
  getRoleColor: (role: string) => string;
}

function MailDetail({ selected, jobRole, workspaceId, userId, formatTime, getRoleColor }: MailDetailProps) {
  const [replyDraft, setReplyDraft] = useState("");
  const [replyState, setReplyState] = useState<"idle" | "generating" | "draft" | "sending" | "sent">("idle");
  const [replyError, setReplyError] = useState<string | null>(null);
  const isReceived = selected.receiver_role === jobRole;

  const handleGenerateReply = async () => {
    setReplyState("generating");
    setReplyError(null);
    try {
      const draft = await generateAIReply(selected, jobRole);
      setReplyDraft(draft);
      setReplyState("draft");
    } catch {
      setReplyError("AI 답장 생성에 실패했습니다. 다시 시도해주세요.");
      setReplyState("idle");
    }
  };

  const handleSendReply = async () => {
    if (!replyDraft.trim() || !workspaceId || !userId) return;
    setReplyState("sending");
    const { error } = await supabase.from("collab_messages").insert({
      workspace_id: workspaceId,
      sender_user_id: userId,
      sender_role: jobRole,
      receiver_role: selected.sender_role,
      task_id: selected.task_id,
      task_title: selected.task_title,
      subject: `RE: ${selected.subject}`,
      body: replyDraft.trim(),
      is_read: false,
    });
    if (error) {
      setReplyError("전송에 실패했습니다.");
      setReplyState("draft");
    } else {
      setReplyState("sent");
    }
  };

  return (
    <div className="max-w-2xl p-6">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-[10px] text-emerald-400/80 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full font-semibold">
            <i className="ri-team-line mr-1"></i>직무간 협업 요청
          </span>
          <span className={`text-[10px] px-2 py-0.5 rounded border ${getRoleColor(selected.sender_role)}`}>
            발신: {ROLE_LABEL[selected.sender_role] ?? selected.sender_role}
          </span>
          <span className="text-[#333333] text-[10px]">→</span>
          <span className={`text-[10px] px-2 py-0.5 rounded border ${getRoleColor(selected.receiver_role)}`}>
            수신: {ROLE_LABEL[selected.receiver_role] ?? selected.receiver_role}
          </span>
        </div>
        <h2 className="text-[#F2F2F2] font-semibold text-base mb-1">{selected.subject}</h2>
        <p className="text-[#3D3D3D] text-[10px]">{formatTime(selected.created_at)}</p>
      </div>

      {/* Task Badge */}
      <div className="flex items-center gap-2 mb-4 px-3 py-2.5 bg-white/[0.02] border border-white/[0.06] rounded-xl">
        <i className="ri-task-line text-[#4B6CF7]/50 shrink-0"></i>
        <div>
          <p className="text-[#3D3D3D] text-[10px] mb-0.5">연관 태스크</p>
          <p className="text-[#888888] text-xs">{selected.task_title}</p>
        </div>
      </div>

      {/* Body */}
      <div className="bg-[#111112] border border-white/[0.06] rounded-xl px-5 py-4 mb-4">
        <p className="text-[#AAAAAA] text-sm leading-relaxed whitespace-pre-wrap">{selected.body}</p>
      </div>

      {/* AI Reply Section - only for received messages */}
      {isReceived && (
        <div className="border border-white/[0.07] rounded-xl overflow-hidden">
          {/* Reply Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#111112] border-b border-white/[0.06]">
            <div className="flex items-center gap-2">
              <i className="ri-robot-2-line text-[#D97706]/70 text-sm"></i>
              <span className="text-[#888888] text-xs font-medium">AI 답장 어시스턴트</span>
              <span className="text-[9px] bg-[#D97706]/10 text-[#D97706] border border-[#D97706]/20 px-1.5 py-0.5 rounded-full font-medium">GEMINI</span>
            </div>
            {replyState === "idle" && (
              <button
                type="button"
                onClick={handleGenerateReply}
                className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg bg-[#D97706]/10 text-[#D97706] border border-[#D97706]/20 hover:bg-[#D97706]/20 transition-colors cursor-pointer whitespace-nowrap font-medium"
              >
                <i className="ri-magic-line text-xs"></i>
                AI 답장 초안 생성
              </button>
            )}
            {replyState === "sent" && (
              <span className="text-[11px] text-emerald-400/80 flex items-center gap-1">
                <i className="ri-check-double-line"></i>답장 전송 완료
              </span>
            )}
          </div>

          {/* Generating state */}
          {replyState === "generating" && (
            <div className="px-4 py-5 flex items-center gap-3 bg-[#0C0C0E]">
              <div className="flex items-center gap-1">
                {[0, 1, 2].map((i) => (
                  <span key={i} className="w-1.5 h-1.5 rounded-full bg-[#D97706]/60 animate-bounce inline-block"
                    style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
              </div>
              <span className="text-[#555555] text-xs">Gemini가 답장 초안을 작성 중입니다...</span>
            </div>
          )}

          {/* Draft + Edit state */}
          {(replyState === "draft" || replyState === "sending") && (
            <div className="bg-[#0C0C0E] px-4 py-3">
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-edit-line text-[#D97706]/50 text-xs"></i>
                <span className="text-[#3D3D3D] text-[10px]">초안을 자유롭게 편집 후 전송하세요</span>
              </div>
              <EtiquetteCoach subject={`RE: ${selected?.subject ?? ""}`} body={replyDraft} />
              <textarea
                value={replyDraft}
                onChange={(e) => setReplyDraft(e.target.value)}
                maxLength={500}
                rows={8}
                className="w-full bg-white/[0.03] text-[#CCCCCC] text-xs leading-relaxed rounded-lg px-3 py-2.5 outline-none border border-white/[0.07] focus:border-[#D97706]/30 resize-none placeholder-[#333333]"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[#2A2A2A] text-[10px]">{replyDraft.length}/500</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setReplyState("idle"); setReplyDraft(""); }}
                    className="text-xs px-3 py-1.5 rounded-lg text-[#555555] border border-white/[0.07] hover:border-white/[0.15] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    onClick={handleSendReply}
                    disabled={!replyDraft.trim() || replyState === "sending"}
                    className="flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white transition-colors cursor-pointer whitespace-nowrap font-medium"
                  >
                    {replyState === "sending" ? (
                      <><div className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin"></div>전송 중</>
                    ) : (
                      <><i className="ri-send-plane-fill"></i>답장 전송</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Sent state */}
          {replyState === "sent" && (
            <div className="px-4 py-4 bg-[#0C0C0E] flex items-center gap-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-400/15">
                <i className="ri-check-double-line text-emerald-400 text-sm"></i>
              </div>
              <div>
                <p className="text-[#888888] text-xs font-medium">답장이 전송되었습니다</p>
                <p className="text-[#333333] text-[10px]">{ROLE_LABEL[selected.sender_role] ?? selected.sender_role}의 인박스에서 확인 가능합니다</p>
              </div>
            </div>
          )}

          {replyError && (
            <div className="px-4 py-2 bg-red-400/5 border-t border-red-400/10">
              <p className="text-red-400/70 text-[11px]">{replyError}</p>
            </div>
          )}

          {/* Idle hint */}
          {replyState === "idle" && (
            <div className="px-4 py-3 bg-[#0C0C0E]">
              <p className="text-[#2A2A2A] text-[10px] leading-relaxed">
                AI가 메일 내용을 분석해 전문적인 답장 초안을 자동으로 작성합니다. 편집 후 전송하세요.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Non-received hint */}
      {!isReceived && (
        <div className="flex items-start gap-2.5 px-3 py-3 bg-[#4B6CF7]/[0.04] border border-[#4B6CF7]/10 rounded-xl">
          <i className="ri-information-line text-[#4B6CF7]/40 shrink-0 mt-0.5"></i>
          <p className="text-[#444444] text-[11px] leading-relaxed">
            보낸 메일입니다. 상대방이 확인하고 답장을 보내면 받은 요청 탭에서 확인할 수 있습니다.
          </p>
        </div>
      )}
    </div>
  );
}

export default function CollabMailbox() {
  const { user } = useAuth();
  const { jobRole } = useRole();
  const [messages, setMessages] = useState<CollabMessage[]>([]);
  const [selected, setSelected] = useState<CollabMessage | null>(null);
  const [tab, setTab] = useState<"received" | "sent">("received");
  const [loading, setLoading] = useState(true);

  const workspaceId = localStorage.getItem("kap_solo_workspace_id");

  const fetchMessages = useCallback(async () => {
    if (!user || !workspaceId) { setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase
      .from("collab_messages")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("created_at", { ascending: false });
    if (data) setMessages(data as CollabMessage[]);
    setLoading(false);
  }, [user, workspaceId]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const markRead = async (msg: CollabMessage) => {
    setSelected(msg);
    if (!msg.is_read && msg.receiver_role === jobRole) {
      await supabase.from("collab_messages").update({ is_read: true }).eq("id", msg.id);
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, is_read: true } : m)));
    }
  };

  const received = messages.filter((m) => m.receiver_role === jobRole);
  const sent = messages.filter((m) => m.sender_user_id === user?.id);
  const list = tab === "received" ? received : sent;
  const unread = received.filter((m) => !m.is_read).length;

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="flex h-full">
      {/* List */}
      <div className="w-[270px] shrink-0 bg-[#111112] border-r border-white/[0.05] flex flex-col">
        <div className="flex gap-1 px-3 py-3 border-b border-white/[0.05]">
          <button
            type="button"
            onClick={() => setTab("received")}
            className={`flex-1 text-xs py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${tab === "received" ? "bg-emerald-400/15 text-emerald-400/90" : "text-[#555555] hover:text-[#888888]"}`}
          >
            받은 요청
            {unread > 0 && (
              <span className="ml-1.5 text-[9px] bg-emerald-400/25 text-emerald-400 px-1 py-0.5 rounded-full">{unread}</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setTab("sent")}
            className={`flex-1 text-xs py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${tab === "sent" ? "bg-[#4B6CF7]/15 text-[#7A9BFF]" : "text-[#555555] hover:text-[#888888]"}`}
          >
            보낸 요청
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-5 h-5 border border-white/20 border-t-[#4B6CF7] rounded-full animate-spin"></div>
            </div>
          ) : list.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <i className="ri-team-line text-2xl text-[#2A2A2A] mb-2"></i>
              <p className="text-[#333333] text-xs">{tab === "received" ? "받은 협업 요청이 없습니다" : "보낸 협업 요청이 없습니다"}</p>
            </div>
          ) : (
            list.map((msg) => {
              const isSelected = selected?.id === msg.id;
              const isMine = tab === "sent";
              const role = isMine ? msg.receiver_role : msg.sender_role;
              return (
                <div
                  key={msg.id}
                  onClick={() => markRead(msg)}
                  className={`px-3 py-3 border-b border-white/[0.03] cursor-pointer transition-all ${isSelected ? "bg-emerald-400/[0.05] border-l-2 border-l-emerald-400/50" : "hover:bg-white/[0.02]"} ${!msg.is_read && !isMine ? "bg-[#141416]" : ""}`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-400/10 shrink-0">
                      <i className="ri-team-line text-emerald-400/60 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${getRoleColor(role)}`}>
                          {isMine ? `→ ${ROLE_LABEL[role] ?? role}` : (ROLE_LABEL[role] ?? role)}
                        </span>
                        <span className="text-[#2A2A2A] text-[9px] shrink-0 ml-1">{formatTime(msg.created_at)}</span>
                      </div>
                      <p className={`text-[11px] truncate mt-0.5 ${!msg.is_read && !isMine ? "text-[#AAAAAA]" : "text-[#555555]"}`}>
                        {msg.subject}
                      </p>
                      <p className="text-[#333333] text-[9px] truncate mt-0.5">{msg.task_title}</p>
                    </div>
                    {!msg.is_read && !isMine && (
                      <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full shrink-0 mt-1.5"></div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Detail */}
      <div className="flex-1 bg-[#0C0C0E] overflow-y-auto">
        {selected ? (
          <MailDetail
            selected={selected}
            jobRole={jobRole}
            workspaceId={workspaceId}
            userId={user?.id}
            formatTime={formatTime}
            getRoleColor={getRoleColor}
          />
        ) : (
          <div className="h-full flex flex-col items-center justify-center">
            <i className="ri-team-line text-3xl text-[#2A2A2A] mb-3"></i>
            <p className="text-[#333333] text-sm">협업 요청 메일을 선택하세요</p>
            <p className="text-[#222222] text-xs mt-1">허브 태스크에서 협업 요청을 보낼 수 있습니다</p>
          </div>
        )}
      </div>
    </div>
  );
}
